import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Mail, Phone, Linkedin } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Footer } from "@/components/utilities/Footer";
import { ThemeToggle } from "@/components/utilities/ThemeToggle";
import {
  Brain,
  Github,
  Zap,
  Database,
  Key,
  Globe,
  FileCode,
  MessageSquare,
  ArrowRight,
  Star,
  Sparkles,
} from "lucide-react";

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  const commandIndex = useRef(0);
  const [currentCommand, setCurrentCommand] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [zoomImage, setZoomImage] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100 } },
  };
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const commands = [
      "pip install docgen-cli",
      "docgen auth login --key=YOUR_API_KEY",
      "docgen generate --current-dir",
      "docgen analyze --repo https://github.com/user/project",
      "docgen export --format markdown --output ./docs",
    ];

    let typingTimer;
    let currentIndex = 0;
    let direction = 1;

    const typeCommand = () => {
      const targetCommand = commands[commandIndex.current];

      if (direction === 1) {
        if (currentIndex <= targetCommand.length) {
          setCurrentCommand(targetCommand.substring(0, currentIndex));
          currentIndex++;
          typingTimer = setTimeout(typeCommand, Math.random() * 50 + 50);
        } else {
          direction = -1;
          typingTimer = setTimeout(typeCommand, 2000);
        }
      } else {
        if (currentIndex >= 0) {
          setCurrentCommand(targetCommand.substring(0, currentIndex));
          currentIndex--;
          typingTimer = setTimeout(typeCommand, Math.random() * 30 + 30);
        } else {
          direction = 1;
          commandIndex.current = (commandIndex.current + 1) % commands.length;
          typingTimer = setTimeout(typeCommand, 500);
        }
      }
    };

    typingTimer = setTimeout(typeCommand, 1000);
    return () => clearTimeout(typingTimer);
  }, []);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    if (user && localStorage.getItem("token")) {
      navigate("/dashboard");
    } else {
      navigate("/auth/login");
    }
  };

  const handleContactUs = () => {
    window.open("https://www.loom.com/share/ba9a74844ce34536bab02f10914b2431?sid=a1daca29-1d20-4207-a4e3-36e5cfffcf4f", "_blank");
  };

  const handleAccessCheck = () => {
    navigate("/auth/login");
  };

  const handleEmailContact = () => {
    window.location.href = "mailto:aniket@docgen.dev";
  };

  const features = [
    {
      icon: <Brain className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Eliminate Manual Effort",
      description: "No more tedious writing of docs that takes time away from coding",
    },
    {
      icon: <Globe className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "End Language Barriers",
      description: "Overcome the challenge of documenting across multiple programming languages",
    },
    {
      icon: <FileCode className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Fix Inconsistent Formatting",
      description: "Say goodbye to messy, disorganized documentation styles",
    },
    {
      icon: <Github className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Solve Code-Doc Disconnect",
      description: "Keep documentation automatically in sync with your code changes",
    },
    {
      icon: <Zap className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Reclaim Dev Time",
      description: "Stop spending hours on documentation instead of building features",
    },
    {
      icon: <Key className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Secure Your Code Knowledge",
      description: "Prevent knowledge loss when team members leave or change roles",
    },
  ];

  const testimonialsData = [
    {
      title: "Speed Up Onboarding",
      text: "Well-documented code reduces the time it takes for new team members to understand and contribute to your codebase.",
      icon: <Brain className="h-6 w-6" />,
    },
    {
      title: "Reduce Support Burden",
      text: "Good documentation means fewer questions and support requests from users and team members.",
      icon: <MessageSquare className="h-6 w-6" />,
    },
    {
      title: "Improve Code Quality",
      text: "The process of documenting forces developers to think more deeply about their code's design and usage.",
      icon: <FileCode className="h-6 w-6" />,
    },
     ];

  // Real testimonials from users
  const userTestimonials = [
    {
      name: "Sarah Chen",
      role: "Senior Developer",
      company: "TechFlow Solutions",
      avatar: "https://ui-avatars.com/api/?name=Sarah+Chen&background=10b981&color=fff&size=150",
      content: "DocGen saved us weeks of documentation work. Our onboarding time for new developers dropped from 3 weeks to just 5 days.",
      rating: 5
    },
    {
      name: "Marcus Rodriguez",
      role: "Engineering Manager",
      company: "DataCore Systems",
      avatar: "https://ui-avatars.com/api/?name=Marcus+Rodriguez&background=059669&color=fff&size=150",
      content: "Finally, documentation that actually stays up-to-date with our code. The automatic sync feature is a game-changer.",
      rating: 5
    },
    {
      name: "Emily Johnson",
      role: "Lead Frontend Developer",
      company: "InnovateLabs",
      avatar: "https://ui-avatars.com/api/?name=Emily+Johnson&background=047857&color=fff&size=150",
      content: "I was skeptical about AI-generated docs, but DocGen produces better documentation than most of our team writes manually.",
      rating: 4.7
    },
    {
      name: "David Park",
      role: "Full Stack Developer",
      company: "CloudBridge Tech",
      avatar: "https://ui-avatars.com/api/?name=David+Park&background=065f46&color=fff&size=150",
      content: "The integration with our existing workflow was seamless. Now documentation is just part of our natural development process.",
      rating: 4.9
    }
  ];

  const teamMembers = [
    {
      name: "Aniket Singh",
      role: "Founder & CEO",
      description: "Leading DocGen with a vision for transforming documentation, Aniket focuses on innovation and developer experience.",
      email: "aniket@docgen.dev",
      linkedin: "https://www.linkedin.com/in/aniket-singh-9a4860190/",
      image: "https://i.ibb.co/67M5RpLc/aniket.jpg",
      phone: "+1 (555) 987-6543",
    },
    {
      name: "Varshith G",
      role: "Co-founder & CTO",
      description: "Driving technical excellence at DocGen, Varshith specializes in building scalable developer tools.",
      email: "varshith@docgen.dev",
      linkedin: "https://www.linkedin.com/in/varshithg17/",
      image: "https://i.ibb.co/0Vm3Q3wh/varshith.jpg",
      phone: "+1 (555) 123-4567",
    },
  ];


  const staggerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
  };

  const letterVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", damping: 12, stiffness: 100 },
    },
  };

  const SplitText = ({ text, className }) => {
    return (
      <motion.span
        variants={staggerVariants}
        initial="hidden"
        animate="visible"
        className={className}
      >
        {text.split("").map((char, index) => (
          <motion.span
            key={index}
            variants={letterVariants}
            className="inline-block"
          >
            {char === " " ? "Hawkins" : char}
          </motion.span>
        ))}
      </motion.span>
    );
  };

  // Function to handle image zoom
  const handleImageClick = (imageSrc) => {
    setZoomImage(imageSrc);
  };

  // Function to close zoomed image
  const closeZoom = () => {
    setZoomImage(null);
  };

  // Make sure we scroll to top when this page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden flex flex-col">
      {/* Image zoom overlay */}
      {zoomImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={closeZoom}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <img 
              src={zoomImage} 
              alt="Zoomed view" 
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button 
              className="absolute top-4 right-4 bg-white/10 text-white rounded-full p-2 hover:opacity-80 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                closeZoom();
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Theme Toggle Button - Positioned at the top right */}
      {/* <div className="absolute top-4 right-4 z-50">
        <ThemeToggle />
      </div> */}
    
      {/* Background elements for hero section only */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-[#030405]' : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
        <div className="absolute inset-0 [mask-image:radial-gradient(ellipse_80%_50%_at_50%_-10%,#000_70%,transparent_100%)]">
          <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-[#030405]' : 'bg-[linear-gradient(to_right,#d9d6c32e_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c32e_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
        </div>
        
        {/* Consistent grid pattern across the entire page for dark mode */}
        {theme === 'dark' && (
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f25_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f25_1px,transparent_1px)] bg-[size:15px_15px] opacity-40"></div>
        )}
        
        {/* White/grey shading elements - only show in light mode */}
        {theme !== 'dark' && (
          <>
            <div className="absolute inset-0 bg-white/20 [mask-image:radial-gradient(ellipse_100%_50%_at_50%_0%,#000_60%,transparent_100%)]"></div>
            <div className="absolute inset-y-0 left-0 right-1/2 bg-white/10 blur-3xl [mask-image:linear-gradient(to_right,#000_20%,transparent_100%)]"></div>
            <div className="absolute inset-y-0 right-0 left-2/3 bg-white/10 blur-3xl [mask-image:linear-gradient(to_left,#000_20%,transparent_100%)]"></div>
          </>
        )}
        
        {/* Gradient orbs */}
        <motion.div
          className={`absolute top-1/4 -right-40 w-96 h-96 rounded-full blur-3xl ${theme === 'dark' 
            ? 'bg-gradient-to-r from-primary/10 to-primary/5' 
            : 'bg-gradient-to-r from-emerald-500/20 to-emerald-400/15 shadow-[0_0_100px_60px_rgba(255,255,255,0.25)]'}`}
          animate={{ x: [0, 60, 0], y: [0, -40, 0], scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -top-20 left-1/3 w-72 h-72 rounded-full blur-3xl ${theme === 'dark' 
            ? 'bg-gradient-to-r from-primary/10 to-primary/5' 
            : 'bg-gradient-to-r from-emerald-600/15 to-emerald-400/10 shadow-[0_0_80px_40px_rgba(255,255,255,0.2)]'}`}
          animate={{ x: [-20, 40, -20], y: [10, 40, 10], scale: [1.1, 0.9, 1.1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute bottom-1/3 -left-20 w-80 h-80 rounded-full blur-3xl ${theme === 'dark' 
            ? 'bg-gradient-to-r from-primary/10 to-primary/5' 
            : 'bg-gradient-to-r from-emerald-700/15 to-emerald-500/10 shadow-[0_0_90px_45px_rgba(255,255,255,0.15)]'}`}
          animate={{ x: [0, -30, 0], y: [0, 50, 0], scale: [1, 1.3, 1], opacity: [0.4, 0.6, 0.4] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      
      {/* Hero section with updated styling */}
      <div className="container mx-auto px-4 sm:px-6 relative z-10 pt-16 sm:pt-20 flex-grow">
        <motion.div
          ref={heroRef}
          className="flex flex-col items-center justify-center min-h-[90vh] py-8 sm:py-16 relative"
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Enhanced light effect circles */}
          <motion.div
            className={`absolute w-32 h-32 border ${theme === 'dark' 
              ? 'border-primary/20' 
              : 'border-emerald-500/20 shadow-[0_0_25px_10px_rgba(255,255,255,0.1)]'} rounded-full`}
            style={{ left: `calc(30% + ${mousePosition.x * -20}px)`, top: `calc(20% + ${mousePosition.y * -20}px)` }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className={`absolute w-40 h-40 border ${theme === 'dark' 
              ? 'border-primary/10' 
              : 'border-emerald-500/10 shadow-[0_0_30px_12px_rgba(255,255,255,0.08)]'} rounded-full`}
            style={{ right: `calc(25% + ${mousePosition.x * 20}px)`, bottom: `calc(30% + ${mousePosition.y * 20}px)` }}
            animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          
          <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-[30vh] ${theme === 'dark' ? 'bg-gradient-to-b from-white/[0.03] to-transparent' : 'bg-gradient-to-b from-white/30 to-transparent'} blur-3xl -z-10`}></div>

          {Array.from({ length: 6 }).map((_, index) => (
            <motion.div
              key={index}
              className={`absolute ${theme === 'dark' ? 'text-primary/20' : 'text-primary-light/20'} text-xs sm:text-sm hidden sm:block`}
              initial={{ x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight, opacity: 0 }}
              animate={{ y: [null, "-100vh"], opacity: [0, 0.7, 0] }}
              transition={{ repeat: Infinity, duration: Math.random() * 20 + 15, delay: Math.random() * 20, ease: "linear" }}
            >
              {["<>", "/>", "{}", "()", "[]", "/**/"][Math.floor(Math.random() * 6)]}
            </motion.div>
          ))}

          <motion.div
            className="text-center max-w-3xl relative z-10"
            variants={itemVariants}
          >
            <motion.div
              className={`mx-auto mb-6 inline-flex items-center rounded-full ${theme === 'dark' ? 'border border-primary/30 bg-primary/10' : 'border border-primary-light/30 bg-primary-light/10'} px-3 py-1 text-sm font-medium ${theme === 'dark' ? 'text-primary' : 'text-primary-light'} backdrop-blur-md`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles className="mr-1 h-3 w-3" />
              New: Solve Undocumented Legacy Code Problems
            </motion.div>

            <div className="relative">
              <motion.h1
                className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight"
                variants={itemVariants}
              >
                <div className="relative inline-block">
                  <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-br from-white to-gray-400' : 'bg-gradient-to-br from-gray-800 to-gray-600'}`}>
                    Documentation that
                  </span>
                </div>
                <br />
                <div className="relative">
                  <span className={`text-transparent bg-clip-text ${theme === 'dark' 
                    ? 'bg-gradient-to-r from-primary via-primary/80 to-primary/60' 
                    : 'bg-gradient-to-r from-primary-light via-emerald-500 to-emerald-600'} relative z-10`}>
                    writes itself
                  </span>
                  <motion.div
                    className={`absolute -inset-1 rounded-lg blur-lg ${theme === 'dark' ? 'bg-primary/30' : 'bg-primary-light/20'}`}
                    animate={{ opacity: [0.4, 0.7, 0.4] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
              </motion.h1>
            </div>

            <motion.p
              className={`text-xl ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-8 max-w-xl mx-auto`}
              variants={itemVariants}
            >
              Generate comprehensive documentation for any codebase in seconds
              with our advanced AI-powered analysis engine.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row justify-center gap-4 mb-16"
              variants={itemVariants}
            >
              <Button
                size="lg"
                className={`${theme === 'dark' 
                  ? 'bg-gradient-to-r from-primary to-primary/80 text-[#0D1117]' 
                  : 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'} hover:opacity-90 px-8 py-6 transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,157,0.3)] relative overflow-hidden group`}
                onClick={handleGetStarted}
              >
                <span className="relative z-10 flex items-center">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className={`${theme === 'dark' 
                  ? 'border-primary/60 text-primary hover:text-primary hover:bg-primary/10' 
                  : 'border-emerald-500/40 text-emerald-600 hover:text-emerald-700 hover:bg-transparent'} px-8 py-6 transition-all duration-300 transform hover:scale-105`}
                onClick={handleContactUs}
              >
                <span className="flex items-center">
                  View Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </Button>
            </motion.div>
          </motion.div>

          {/* Product Showcase Section */}
          <motion.div
            className={`py-16 border-t ${theme === 'dark' ? 'border-gray-800/50' : 'border-gray-300/50'}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="text-center max-w-3xl mx-auto mb-14">
              <motion.h2
                className="text-3xl sm:text-4xl font-bold mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-gray-400' : 'bg-gradient-to-r from-gray-800 to-gray-600'}`}>
                  See DocGen in action
                </span>
              </motion.h2>
              <motion.p
                className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} max-w-xl mx-auto`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                viewport={{ once: true }}
              >
                Experience how our powerful documentation platform transforms code into comprehensive documentation
              </motion.p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Architecture Diagram */}
                <motion.div
                  className="group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  viewport={{ once: true }}
                >
                  <div 
                    className="relative rounded-xl overflow-hidden bg-[#0D1117] border border-gray-700/50 shadow-[0_10px_40px_-15px_rgba(255,255,255,0.1)] transition-all duration-500 group-hover:shadow-[0_20px_60px_-15px_rgba(255,255,255,0.2)] cursor-pointer"
                    onClick={() => handleImageClick("/architecture-demo.gif")}
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gray-500/50 to-white/30 opacity-50"></div>
                    
                    <div style={{ minHeight: "350px" }}>
                      <img 
                        src="/architecture-demo.gif" 
                        alt="Architecture Demo" 
                        className="w-full h-full object-cover scale-110 transition-transform duration-700 group-hover:scale-125"
                      />
                    </div>
                    
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0D1117] to-transparent py-6 px-5 rounded-b-lg">
                      <h3 className="text-xl font-medium text-white">Architecture Overview</h3>
                      <p className="text-gray-400 text-sm mt-auto">Visualize how your components work together</p>
                    </div>
                  </div>
                </motion.div>

                {/* Documentation */}
                <motion.div
                  className="group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.7 }}
                  viewport={{ once: true }}
                >
                  <div 
                    className="relative rounded-xl overflow-hidden bg-[#0D1117] border border-gray-700/50 shadow-[0_10px_40px_-15px_rgba(255,255,255,0.1)] transition-all duration-500 group-hover:shadow-[0_20px_60px_-15px_rgba(255,255,255,0.2)] cursor-pointer"
                    onClick={() => handleImageClick("/client-documentation.png")}
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gray-500/50 to-white/30 opacity-50"></div>
                    
                    <div style={{ minHeight: "300px" }}>
                      <img 
                        src="/client-documentation.png" 
                        alt="Client Documentation" 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0D1117] to-transparent py-6 px-5 rounded-b-lg">
                      <h3 className="text-xl font-medium text-white">Auto-Generated Documentation</h3>
                      <p className="text-gray-400 text-sm mt-auto">Detailed code documentation created automatically</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Integrations Section with background */}
      <div className={`w-full ${theme === 'dark' 
        ? 'bg-[#030405]' 
        : 'bg-gradient-to-b from-white to-[#f8fcfa]'} relative z-10`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute inset-0 ${theme === 'dark'
            ? 'bg-[linear-gradient(to_right,#4f4f4f1a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f1a_1px,transparent_1px)]'
            : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
          <motion.div
            className={`absolute top-1/4 right-1/4 w-[550px] h-[550px] rounded-full opacity-10 blur-3xl ${theme === 'dark'
              ? 'bg-gradient-to-br from-emerald-700/20 via-emerald-600/15 to-transparent'
              : 'bg-gradient-to-br from-emerald-400/20 via-emerald-500/15 to-transparent'}`}
            animate={{
              y: [0, -50, 0],
              x: [0, 30, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        
        <motion.div
          className="container mx-auto px-4 sm:px-6 py-16 sm:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="text-center max-w-3xl mx-auto mb-14">
            <motion.h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-gray-400' : 'bg-gradient-to-r from-gray-800 to-gray-600'}`}>
                Powerful Integrations
              </span>
            </motion.h2>
            <motion.p
              className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} max-w-xl mx-auto`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              Connect DocGen with your favorite tools to enhance your documentation workflow
            </motion.p>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Active Integrations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {/* GitHub Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 relative">
                  <div className={`absolute -top-1 -right-1 ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'badge-available'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Available Now
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="/github-logo.png" 
                        alt="GitHub Logo" 
                        className="w-8 h-8"
                        onError={(e) => {
                          e.currentTarget.src = "https://github.githubassets.com/assets/GitHub-Mark-ea2971cee799.png";
                        }}
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>GitHub</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Code repositories</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Import repositories into your projects
                  </p>
                </div>
              </motion.div>

              {/* Notion Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 relative">
                  <div className={`absolute -top-1 -right-1 ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'badge-available'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Available Now
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="/notion-logo.png" 
                        alt="Notion Logo" 
                        className="w-8 h-8"
                        onError={(e) => {
                          e.currentTarget.src = "https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png";
                        }}
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>Notion</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Workspace docs</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Export docs to your Notion workspace
                  </p>
                </div>
              </motion.div>

              {/* Confluence Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? '' : 'coming-soon'} ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 h-full flex flex-col relative">
                  <div className={`absolute -top-1 -right-1 ${theme === 'dark' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'badge-coming-soon'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Coming Soon
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="https://cdn.worldvectorlogo.com/logos/confluence-1.svg" 
                        alt="Confluence Logo" 
                        className="w-8 h-8"
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>Confluence</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Wiki docs</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Export documentation to your Confluence wiki
                  </p>
                </div>
              </motion.div>

              {/* Jira Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? '' : 'coming-soon'} ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 h-full flex flex-col relative">
                  <div className={`absolute -top-1 -right-1 ${theme === 'dark' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'badge-coming-soon'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Coming Soon
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="https://cdn.worldvectorlogo.com/logos/jira-1.svg" 
                        alt="Jira Logo" 
                        className="w-8 h-8"
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>Jira</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Issue tracking</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Link documentation to your Jira issues
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Second Row of Integrations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mt-6">
              {/* Bitbucket Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? '' : 'coming-soon'} ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                viewport={{ once: true, margin: "-5%" }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 h-full flex flex-col relative">
                  <div className={`absolute -top-1 -right-1 z-10 ${theme === 'dark' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'badge-coming-soon'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Coming Soon
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="https://cdn.worldvectorlogo.com/logos/bitbucket-icon.svg" 
                        alt="Bitbucket Logo" 
                        className="w-8 h-8"
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>Bitbucket</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Code repositories</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Import repositories from Bitbucket
                  </p>
                </div>
              </motion.div>

              {/* GitLab Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? '' : 'coming-soon'} ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 h-full flex flex-col relative">
                  <div className={`absolute -top-1 -right-1 ${theme === 'dark' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'badge-coming-soon'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Coming Soon
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="https://cdn.worldvectorlogo.com/logos/gitlab.svg" 
                        alt="GitLab Logo" 
                        className="w-8 h-8"
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>GitLab</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Code repositories</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Import repositories from GitLab
                  </p>
                </div>
              </motion.div>

              {/* Slack Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? '' : 'coming-soon'} ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 h-full flex flex-col relative">
                  <div className={`absolute -top-1 -right-1 ${theme === 'dark' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'badge-coming-soon'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Coming Soon
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg" 
                        alt="Slack Logo" 
                        className="w-8 h-8"
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>Slack</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Team communication</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Receive documentation updates in Slack
                  </p>
                </div>
              </motion.div>

              {/* Tableau Integration Tile */}
              <motion.div
                className={`integration-card ${theme === 'dark' ? '' : 'coming-soon'} ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border border-gray-200/60 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
              >
                <div className="p-4 h-full flex flex-col relative">
                  <div className={`absolute -top-1 -right-1 ${theme === 'dark' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'badge-coming-soon'} text-xs font-medium px-2 py-1 rounded-md transform rotate-2`}>
                    Coming Soon
                  </div>
                  <div className="flex items-center mb-3">
                    <div className="bg-white p-2 rounded-md mr-3">
                      <img 
                        src="https://cdn.worldvectorlogo.com/logos/tableau-software.svg" 
                        alt="Tableau Logo" 
                        className="w-8 h-8"
                      />
                    </div>
                    <div>
                      <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} font-medium`}>Tableau</h3>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Data visualization</p>
                    </div>
                  </div>
                  
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm mt-auto`}>
                    Visualize documentation metrics
                  </p>
                </div>
              </motion.div>
            </div>

            {/* More Integrations */}
            <div className="mt-8 text-center">
              <motion.div 
                className={`inline-flex items-center ${theme === 'dark' ? 'bg-gray-800/30' : 'bg-gray-200/50'} px-4 py-2 rounded-full ${theme === 'dark' ? 'border border-gray-700/50' : 'border border-gray-300/50'} ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <span className="text-sm">More integrations coming soon</span>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats Section with background */}
      <div className={`w-full ${theme === 'dark' 
        ? 'bg-[#030405]' 
        : 'bg-gradient-to-b from-[#f8fcfa] to-[#e7f3ee]'} relative z-10`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute inset-0 ${theme === 'dark'
            ? 'bg-[linear-gradient(to_right,#4f4f4f1a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f1a_1px,transparent_1px)]'
            : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
          <motion.div
            className={`absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl ${theme === 'dark'
              ? 'bg-gradient-to-tl from-emerald-700/20 via-emerald-600/15 to-transparent'
              : 'bg-gradient-to-tl from-emerald-400/15 via-emerald-500/10 to-transparent'}`}
            animate={{
              y: [0, -40, 0],
              x: [0, -30, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        
        <motion.div
          className="container mx-auto px-4 sm:px-6 py-16 sm:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "10000+", label: "Files Documented", icon: <FileCode className="h-6 w-6" /> },
              { value: "40+", label: "Active Users", icon: <Database className="h-6 w-6" /> },
              { value: "20+", label: "Languages", icon: <Globe className="h-6 w-6" /> },
              { value: "4.7/5", label: "User Rating", icon: <Star className="h-6 w-6" /> },
            ].map((metric, index) => (
              <motion.div
                key={index}
                className={`metric-card text-center p-6 rounded-xl ${theme === 'dark' ? 'hover:bg-gray-800/30' : 'hover:bg-white'} transition-all duration-500 ${theme === 'dark' ? 'border border-transparent hover:border-gray-700/50' : 'border border-transparent hover:border-gray-300/70'} relative overflow-hidden group`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <motion.div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-br from-primary/5 to-primary/5' : 'bg-gradient-to-br from-primary-light/5 to-emerald-500/5'} opacity-0 group-hover:opacity-0 transition-opacity duration-500`} />
                <div className="flex flex-col items-center">
                  <div className={`mb-3 ${theme === 'dark' ? 'text-primary group-hover:text-primary' : 'text-emerald-600 group-hover:text-emerald-800'} transition-colors duration-200`}>
                    {metric.icon}
                  </div>
                  <div className={`text-4xl font-bold text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-primary to-primary/80' : 'bg-gradient-to-r from-primary to-emerald-500'} mb-2`}>
                    {metric.value}
                  </div>
                  <div className={`${theme === 'dark' ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-600 group-hover:text-gray-700'} transition-colors duration-200`}>
                    {metric.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* User Testimonials Section */}
      <div className={`w-full ${theme === 'dark' 
        ? 'bg-[#030405]' 
        : 'bg-gradient-to-b from-[#e7f3ee] to-white'} relative z-10`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute inset-0 ${theme === 'dark'
            ? 'bg-[linear-gradient(to_right,#4f4f4f1a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f1a_1px,transparent_1px)]'
            : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
          <motion.div
            className={`absolute top-1/2 right-1/4 w-[300px] h-[300px] rounded-full opacity-15 blur-3xl ${theme === 'dark'
              ? 'bg-gradient-to-bl from-emerald-600/20 via-primary/15 to-transparent'
              : 'bg-gradient-to-bl from-emerald-400/15 via-primary-light/10 to-transparent'}`}
            animate={{
              y: [0, -20, 0],
              x: [0, 15, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 16,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        
        <motion.div
          className="container mx-auto px-4 sm:px-6 py-16 sm:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="text-center max-w-3xl mx-auto mb-14">
            <motion.h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-gray-400' : 'bg-gradient-to-r from-gray-800 to-gray-600'}`}>
                Loved by developers worldwide
              </span>
            </motion.h2>
            <motion.p
              className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} max-w-xl mx-auto`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              Join thousands of developers who have transformed their documentation workflow with DocGen
            </motion.p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {userTestimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  className={`testimonial-card ${theme === 'dark' 
                    ? 'bg-gray-900/30 border border-gray-800 hover:border-gray-700/70' 
                    : 'bg-white/90 border border-gray-200/60 hover:border-gray-300/80'} 
                    rounded-xl p-6 transition-all duration-300 relative overflow-hidden group hover:shadow-lg`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className={`absolute inset-0 ${theme === 'dark' 
                    ? 'bg-gradient-to-br from-primary/5 to-emerald-600/5' 
                    : 'bg-gradient-to-br from-emerald-50/50 to-primary-light/5'} 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  
                  <div className="relative z-10">
                    {/* Rating Stars */}
                    <div className="flex items-center mb-4">
                      {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'} fill-current`} 
                        />
                      ))}
                      {testimonial.rating % 1 !== 0 && (
                        <div className="relative w-4 h-4">
                          <Star className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'} absolute`} />
                          <Star 
                            className={`w-4 h-4 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'} fill-current absolute`}
                            style={{ clipPath: `inset(0 ${100 - (testimonial.rating % 1) * 100}% 0 0)` }}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Testimonial Content */}
                    <blockquote className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6 text-lg leading-relaxed`}>
                      "{testimonial.content}"
                    </blockquote>
                    
                    {/* Author Info */}
                    <div className="flex items-center">
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-transparent group-hover:border-primary/30 transition-colors duration-300"
                      />
                      <div>
                        <div className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                          {testimonial.name}
                        </div>
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {testimonial.role}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-primary/80' : 'text-emerald-600'} font-medium`}>
                          {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {/* Trust indicators */}
            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className={`inline-flex items-center space-x-6 px-6 py-3 rounded-full ${theme === 'dark' 
                ? 'bg-gray-800/30 border border-gray-700/50' 
                : 'bg-gray-100/50 border border-gray-200/50'}`}>
                <div className="flex items-center space-x-2">
                  <Star className={`w-5 h-5 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'} fill-current`} />
                  <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>4.7/5</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>average rating</span>
                </div>
                <div className={`w-px h-6 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'}`} />
                <div className="flex items-center space-x-2">
                  <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>40+</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>happy users</span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Challenges We Solve Section with background */}
      <div className={`w-full ${theme === 'dark' 
        ? 'bg-[#030405]' 
        : 'bg-gradient-to-b from-white to-[#f8fcfa]'} relative z-10`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute inset-0 ${theme === 'dark'
            ? 'bg-[linear-gradient(to_right,#4f4f4f1a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f1a_1px,transparent_1px)]'
            : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
          <motion.div
            className={`absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl ${theme === 'dark'
              ? 'bg-gradient-to-br from-emerald-700/15 via-emerald-600/20 to-transparent'
              : 'bg-gradient-to-br from-emerald-500/15 via-emerald-400/20 to-transparent'}`}
            animate={{
              y: [0, 30, 0],
              x: [0, 20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        
        <motion.div
          className="container mx-auto px-4 sm:px-6 py-16 sm:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="text-center max-w-3xl mx-auto mb-14">
            <motion.h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-gray-400' : 'bg-gradient-to-r from-gray-800 to-gray-600'}`}>
                The challenges we solve
              </span>
            </motion.h2>
            <motion.p
              className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} max-w-xl mx-auto`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              Documentation is often neglected, outdated, or inconsistent. DocGen
              eliminates these pain points so you can ship better software faster.
            </motion.p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className={`feature-card ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border-gray-200/50 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors p-6`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
                >
                  <div className={`mb-4 p-3 ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-100'} inline-block rounded-xl ${theme === 'dark' ? 'border border-gray-800' : 'border border-gray-200/80'}`}>
                    <div className="w-10 h-10 flex items-center justify-center">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className={`text-xl font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-800'} mb-2`}>{feature.title}</h3>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Documentation Matters Section with background */}
      <div className={`w-full ${theme === 'dark' 
        ? 'bg-[#030405]' 
        : 'bg-gradient-to-b from-[#f8fcfa] to-[#e7f3ee]'} relative z-10`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute inset-0 ${theme === 'dark'
            ? 'bg-[linear-gradient(to_right,#4f4f4f1a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f1a_1px,transparent_1px)]'
            : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
          <motion.div
            className={`absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl ${theme === 'dark'
              ? 'bg-gradient-to-tr from-emerald-700/15 via-emerald-600/20 to-transparent'
              : 'bg-gradient-to-tr from-emerald-500/15 via-emerald-400/20 to-transparent'}`}
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 22,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        
        <motion.div
          className="container mx-auto px-4 sm:px-6 py-16 sm:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="text-center max-w-3xl mx-auto mb-14">
            <motion.h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-gray-400' : 'bg-gradient-to-r from-gray-800 to-gray-600'}`}>
                Why documentation matters
              </span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              {testimonialsData.map((benefit, index) => (
                <motion.div
                  key={index}
                  className={`testimonial-card rounded-xl ${theme === 'dark' ? 'bg-gray-900/20 border border-gray-800' : 'bg-white/90 border border-gray-200/60'} p-6 ${theme === 'dark' ? 'hover:border-gray-700/70' : 'hover:border-gray-300/80'} transition-all duration-300 relative overflow-hidden group`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className={`absolute h-1 top-0 left-0 right-0 bg-gradient-to-r ${theme === 'dark' ? 'from-primary/80 to-emerald-500/80' : 'from-primary-light/80 to-emerald-500/80'} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />
                  <div className="flex flex-col items-center text-center">
                    <div className={`h-12 w-12 rounded-full ${theme === 'dark' ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-emerald-100 text-emerald-600 border border-emerald-500/30'} flex items-center justify-center mb-4`}>
                      {benefit.icon}
                    </div>
                    <h3 className={`text-xl font-medium mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{benefit.title}</h3>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{benefit.text}</p>
                  </div>
                  <div className={`absolute -bottom-4 -right-4 w-24 h-24 opacity-5 ${theme === 'dark' ? 'text-primary' : 'text-emerald-600'}`}>
                    <MessageSquare className="w-full h-full" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Documentation Pain Section with background - HIDDEN */}
      {/* 
      <div className={`w-full ${theme === 'dark' 
        ? 'bg-[#030405]' 
        : 'bg-gradient-to-b from-white to-[#f8fcfa]'} relative z-10`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute inset-0 ${theme === 'dark'
            ? 'bg-[linear-gradient(to_right,#4f4f4f1a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f1a_1px,transparent_1px)]'
            : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
          <motion.div
            className={`absolute top-1/3 right-0 w-[450px] h-[450px] rounded-full opacity-10 blur-3xl ${theme === 'dark'
              ? 'bg-gradient-to-bl from-emerald-700/20 via-emerald-600/15 to-transparent'
              : 'bg-gradient-to-bl from-emerald-600/15 via-emerald-500/10 to-transparent'}`}
            animate={{
              y: [0, 40, 0],
              x: [0, -20, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        
        <motion.div
          className="container mx-auto px-4 sm:px-6 py-16 sm:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="max-w-3xl mx-auto text-center mb-12">
            <motion.h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-gray-400' : 'bg-gradient-to-r from-gray-800 to-gray-600'}`}>
                From documentation pain to gain
              </span>
            </motion.h2>
            <motion.p
              className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} max-w-xl mx-auto`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              Transform the most dreaded part of development into a seamless, automated process
            </motion.p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  number: "01",
                  title: "No More Manual Setup",
                  description: "Link your repo and stop writing from scratch",
                  icon: <Brain className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
                },
                {
                  number: "02",
                  title: "End Context Switching",
                  description: "AI reads your code so you don't have to explain it",
                  icon: <Globe className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
                },
                {
                  number: "03",
                  title: "Eliminate Format Headaches",
                  description: "Get consistent docs in your preferred format",
                  icon: <FileCode className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className={`relative ${theme === 'dark' ? 'border border-gray-800 bg-gray-800/30 hover:bg-gray-800/50' : 'border-gray-200/50 bg-white/80 hover:bg-white'} rounded-xl overflow-hidden transition-colors p-6`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5, boxShadow: theme === 'dark' ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" : "0 10px 30px -15px rgba(0, 204, 122, 0.15)" }}
                >
                  <div className="flex items-start">
                    <div className={`mr-4 flex-shrink-0 ${theme === 'dark' ? 'text-primary bg-primary/10' : 'text-emerald-600 bg-emerald-100'} w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg border ${theme === 'dark' ? 'border-primary/30' : 'border-emerald-200'}`}>
                      {step.number}
                    </div>
                    <div>
                      <h3 className={`text-xl font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-800'} mb-2`}>{step.title}</h3>
                      <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{step.description}</p>
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 opacity-10">
                    <div className="w-12 h-12">
                      {step.icon}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-16 flex justify-center">
              <motion.div
                className={`inline-block py-3 px-6 rounded-full ${theme === 'dark' ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center space-x-2">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-sm font-medium">Experience the difference with DocGen</span>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
      */}

      {/* FAQ Section */}
      <div className={`w-full ${theme === 'dark' 
        ? 'bg-[#030405]' 
        : 'bg-gradient-to-b from-[#e7f3ee] to-white'} relative z-10`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute inset-0 ${theme === 'dark'
            ? 'bg-[linear-gradient(to_right,#4f4f4f1a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f1a_1px,transparent_1px)]'
            : 'bg-[linear-gradient(to_right,#d9d6c31a_1px,transparent_1px),linear-gradient(to_bottom,#d9d6c31a_1px,transparent_1px)]'} bg-[size:14px_24px]`} />
          <motion.div
            className={`absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl ${theme === 'dark'
              ? 'bg-gradient-to-br from-emerald-600/15 via-primary/10 to-transparent'
              : 'bg-gradient-to-br from-emerald-400/10 via-primary-light/8 to-transparent'}`}
            animate={{
              y: [0, -25, 0],
              x: [0, 20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
        
        <motion.div
          className="container mx-auto px-4 sm:px-6 py-16 sm:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="text-center max-w-3xl mx-auto mb-14">
            <motion.h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-gray-400' : 'bg-gradient-to-r from-gray-800 to-gray-600'}`}>
                Frequently Asked Questions
              </span>
            </motion.h2>
            <motion.p
              className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} max-w-xl mx-auto`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              Get answers to common questions about DocGen and AI-powered documentation
            </motion.p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {[
                {
                  question: "How does DocGen analyze my code?",
                  answer: "DocGen uses advanced AI to understand your codebase structure, analyze functions, classes, and dependencies to generate comprehensive documentation that explains what your code does and how it works."
                },
                {
                  question: "What export formats are currently available?",
                  answer: "Currently, we support Markdown and Notion integration. We're actively working on adding HTML, PDF, and Confluence support in the coming months."
                },
                {
                  question: "Can I connect my GitHub repository directly?",
                  answer: "Yes! You can easily import your GitHub repositories into DocGen. Simply connect your account and select the repos you want to document."
                },
                {
                  question: "How accurate is the AI-generated documentation?",
                  answer: "Our AI achieves high accuracy by analyzing code context, function relationships, and existing comments. The documentation captures both technical details and business logic effectively."
                },
                {
                  question: "What programming languages do you support?",
                  answer: "We support 20+ programming languages including JavaScript, TypeScript, Python, Java, Go, Rust, C++, PHP, and many more. If your language isn't supported yet, let us know!"
                },
                {
                  question: "Is my code data secure and private?",
                  answer: "Absolutely. Your code is processed securely with enterprise-grade encryption, never stored permanently, and we never share or train on your proprietary code."
                },
                {
                  question: "Can I edit the generated documentation?",
                  answer: "You can review the generated documentation before exporting. Editing feature will be rolled out soon. DocGen provides a solid foundation that you can customize to fit your specific needs."
                },
                {
                  question: "Do you offer team collaboration features?",
                  answer: "Team collaboration features are coming soon! We're working on shared workspaces, team management, and collaborative editing capabilities for enterprise users."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  className={`faq-item ${theme === 'dark' 
                    ? 'bg-gray-900/30 border border-gray-800 hover:border-gray-700/70' 
                    : 'bg-white/90 border border-gray-200/60 hover:border-gray-300/80'} 
                    rounded-xl transition-all duration-300 relative overflow-hidden group`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <details className="group/details">
                    <summary className={`cursor-pointer p-6 ${theme === 'dark' ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50'} transition-colors duration-200 list-none`}>
                      <div className="flex items-center justify-between">
                        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-800'} pr-4`}>
                          {faq.question}
                        </h3>
                        <div className={`flex-shrink-0 ${theme === 'dark' ? 'text-primary' : 'text-emerald-600'} transition-transform duration-200 group-open/details:rotate-45`}>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </summary>
                    <div className="px-6 pb-6">
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                </motion.div>
              ))}
            </div>

            {/* Contact for more questions */}
            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className={`inline-flex items-center space-x-4 px-6 py-4 rounded-xl ${theme === 'dark' 
                ? 'bg-gray-800/30 border border-gray-700/50' 
                : 'bg-gray-100/50 border border-gray-200/50'}`}>
                <MessageSquare className={`w-5 h-5 ${theme === 'dark' ? 'text-primary' : 'text-emerald-600'}`} />
                <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Still have questions?{' '}
                  <button 
                    onClick={handleEmailContact}
                    className={`${theme === 'dark' ? 'text-primary hover:text-primary/80' : 'text-emerald-600 hover:text-emerald-700'} font-medium transition-colors underline`}
                  >
                    Contact us
                  </button>
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Call To Action */}
      <motion.div
        className={`rounded-2xl p-12 ${theme === 'dark' 
          ? 'bg-[#030405] border border-gray-800/50' 
          : 'bg-gradient-to-r from-[#f1f8f5]/90 to-[#e7f3ee]/90 backdrop-blur-sm border border-emerald-200/40'} relative overflow-hidden`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true, margin: "-5%" }}
      >
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className={`absolute -top-60 -right-20 w-96 h-96 rounded-full blur-3xl ${theme === 'dark' 
              ? 'bg-gradient-to-b from-emerald-600/10 to-emerald-500/5' 
              : 'bg-gradient-to-b from-emerald-500/10 to-emerald-400/5'}`}
            animate={{ 
              y: [0, 20, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center">
          <motion.h2
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 max-w-3xl ${theme === 'dark' 
              ? 'text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400' 
              : 'text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-600'}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Ready to transform your documentation workflow?
          </motion.h2>
          
          <motion.p
            className={`text-lg mb-8 max-w-2xl ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            viewport={{ once: true }}
          >
            Start documenting your code smarter, not harder. Try DocGen today.
          </motion.p>
          
          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Button
              size="lg"
              className={`${theme === 'dark' 
                ? 'bg-gradient-to-r from-primary to-primary/80 text-[#0D1117]' 
                : 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'} hover:opacity-90 px-8 py-6 transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,157,0.3)] relative overflow-hidden group`}
              onClick={handleGetStarted}
            >
              <span className="relative z-10 flex items-center">
                Get Started
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              className={`${theme === 'dark' 
                ? 'border-primary/60 text-primary hover:text-primary hover:bg-primary/10' 
                : 'border-emerald-500/40 text-emerald-600 hover:text-emerald-700 hover:bg-transparent'} px-8 py-6 transition-all duration-300 transform hover:scale-105`}
              onClick={handleContactUs}
            >
              <span className="flex items-center">
                View Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </span>
            </Button>
          </motion.div>
        </div>
      </motion.div>

      <Footer />
    </div>
  );
};

const PlayIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

export default Index;